import React from 'react'
import styled, { css, keyframes } from 'styled-components'

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
`

const COLUMNS = 4
const CARDS_PER_COLUMN = [3, 2, 2, 1]
const ROWS = 8

const TaskListSkeleton = ({ view }) => {
  if (view === 'progress-tracker') {
    return (
      <Board aria-busy='true' aria-label='Loading your tasks'>
        {Array.from({ length: COLUMNS }, (_, c) => (
          <Column key={c}>
            <ColumnHead>
              <Dot />
              <Line $w='45%' />
              <Pill $w='26px' />
            </ColumnHead>
            {Array.from({ length: CARDS_PER_COLUMN[c] }, (_, i) => (
              <Card key={i}>
                <Line $w='85%' />
                <Line $w='60%' />
                <Row>
                  <Pill $w='64px' />
                  <Pill $w='48px' />
                </Row>
              </Card>
            ))}
          </Column>
        ))}
        <Hint>Loading your tasks…</Hint>
      </Board>
    )
  }

  if (view === 'table' || view === 'list') {
    return (
      <Rows aria-busy='true' aria-label='Loading your tasks'>
        {Array.from({ length: ROWS }, (_, i) => (
          <TableRow key={i}>
            <Box />
            <Cell $grow>
              <Line $w={i % 2 ? '55%' : '70%'} />
              <Line $w='35%' $h='9px' />
            </Cell>
            <Cell $hideMobile><Pill $w='72px' /></Cell>
            <Cell $hideMobile><Line $w='80px' /></Cell>
            <Cell><Pill $w='56px' /></Cell>
          </TableRow>
        ))}
        <Hint>Loading your tasks…</Hint>
      </Rows>
    )
  }

  return (
    <Cards aria-busy='true' aria-label='Loading your tasks'>
      {Array.from({ length: 4 }, (_, i) => (
        <Card key={i}>
          <Line $w='40%' $h='9px' />
          <Line $w='30%' $h='22px' />
          <Line $w='70%' />
        </Card>
      ))}
      <Hint>Loading your tasks…</Hint>
    </Cards>
  )
}

export default TaskListSkeleton

const shimmer = css`
  background: #e7e9ee;
  border-radius: 6px;
  animation: ${pulse} 1.3s ease-in-out infinite;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`

const Line = styled.span`
  ${shimmer}
  display: block;
  height: ${({ $h }) => $h || '11px'};
  width: ${({ $w }) => $w || '100%'};
`

const Pill = styled.span`
  ${shimmer}
  display: inline-block;
  height: 20px;
  width: ${({ $w }) => $w || '48px'};
  border-radius: 999px;
`

const Dot = styled.span`
  ${shimmer}
  width: 10px; height: 10px; border-radius: 50%; flex: 0 0 auto;
`

const Box = styled.span`
  ${shimmer}
  width: 18px; height: 18px; border-radius: 4px; flex: 0 0 auto;
`

const Board = styled.div`
  display: grid;
  grid-template-columns: repeat(${COLUMNS}, minmax(0, 1fr));
  gap: 12px;
  padding: 8px 0 16px;
  @media (max-width: 768px) { grid-template-columns: minmax(0, 1fr); }
`

const Column = styled.div`
  display: flex; flex-direction: column; gap: 10px;
  padding: 12px; border-radius: 12px; background: #f6f7fb; min-width: 0;
  @media (max-width: 768px) { &:nth-child(n + 3) { display: none; } }
`

const ColumnHead = styled.div`
  display: flex; align-items: center; gap: 8px; margin-bottom: 4px;
  & > span:nth-child(2) { flex: 1; }
`

const Card = styled.div`
  display: flex; flex-direction: column; gap: 10px;
  padding: 14px; border-radius: 10px; background: #fff; border: 1px solid #eceef3;
`

const Row = styled.div`
  display: flex; gap: 8px;
`

const Rows = styled.div`
  display: flex; flex-direction: column;
  border: 1px solid #eceef3; border-radius: 12px; background: #fff; overflow: hidden;
  margin: 8px 0 16px;
`

const TableRow = styled.div`
  display: flex; align-items: center; gap: 16px;
  min-height: 56px; padding: 10px 16px;
  border-bottom: 1px solid #f2f4f7;
  &:last-of-type { border-bottom: none; }
`

const Cell = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  ${({ $grow }) => ($grow ? 'flex: 1; min-width: 0;' : 'flex: 0 0 auto;')}
  @media (max-width: 768px) { ${({ $hideMobile }) => ($hideMobile ? 'display: none;' : '')} }
`

const Cards = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 220px), 1fr)); gap: 12px;
  padding: 8px 0 16px;
`

const Hint = styled.p`
  grid-column: 1 / -1;
  margin: 4px 0 0; padding: 0 16px 12px;
  font-size: 13px; color: #667085;
  ${Board} &, ${Cards} & { padding: 0; }
`
